// Job Journey — tracked jobs by stage with AI insights (premium).
// AI runs when user selects a job in Interview or Offered; results cached per session.

import { useState } from "react";
import {
  Briefcase,
  ChevronRight,
  Loader2,
  Lock,
  Sparkles,
  MessageSquare,
  AlertTriangle,
  CheckCircle,
  DollarSign,
  HelpCircle,
  Copy,
  Globe,
  type LucideIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Application } from "@/lib/storage";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface Props {
  applications: Application[];
  /** Paying subscribers only (not trial) — trial sees preview overlay */
  isSubscribed: boolean;
}

const REGIONS = [
  { value: "US/Canada", label: "US / Canada" },
  { value: "EU", label: "Europe" },
  { value: "Asia", label: "Asia" },
  { value: "Africa", label: "Africa" },
];

const STATUS_STYLES: Record<string, string> = {
  applied:
    "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300",
  interviewing:
    "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300",
  offered:
    "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300",
  rejected:
    "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300",
};

const STAGE_LABELS: Record<string, string> = {
  applied: "Applied",
  interviewing: "Interview",
  offered: "Offered",
  rejected: "Rejected",
};

type InsightPayload = Record<string, unknown>;

interface InsightCache {
  [appId: string]: {
    stage: string;
    data: InsightPayload;
  };
}

export default function JobJourneyTab({ applications, isSubscribed }: Props) {
  const [selectedApp, setSelectedApp] = useState<Application | null>(null);
  const [loading, setLoading] = useState(false);
  const [insights, setInsights] = useState<InsightCache>({});
  const [selectedRegion, setSelectedRegion] = useState("US/Canada");

  const trackedJobs = applications.filter(
    (a) => a.company && a.position && a.company !== "Unknown Company"
  );

  const stages = ["applied", "interviewing", "offered", "rejected"] as const;

  const handleGetInsights = async (app: Application) => {
    if (!isSubscribed) return;

    const stage = app.status;
    if (stage !== "interviewing" && stage !== "offered") {
      toast.info("Insights are available for Interview and Offer stages.");
      return;
    }

    if (insights[app.id]?.stage === stage) {
      setSelectedApp(app);
      return;
    }

    setSelectedApp(app);
    setLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke("job-journey", {
        body: {
          stage,
          company: app.company,
          position: app.position,
          region: stage === "offered" ? selectedRegion : undefined,
        },
      });

      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);

      setInsights((prev) => ({
        ...prev,
        [app.id]: { stage, data: data as InsightPayload },
      }));
    } catch (err: unknown) {
      console.error("Job journey error:", err);
      toast.error(
        err instanceof Error ? err.message : "Failed to generate insights"
      );
    } finally {
      setLoading(false);
    }
  };

  if (!isSubscribed) {
    return (
      <div className="space-y-6 animate-fade-up">
        <div>
          <h3 className="font-display font-bold text-lg text-foreground">
            Job Journey
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            AI-powered guidance at every stage of your job search.
          </p>
        </div>

        <div className="rounded-xl border-2 border-dashed border-primary/30 bg-primary/5 p-8 text-center space-y-4">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto">
            <Lock className="w-7 h-7 text-primary" />
          </div>
          <div>
            <h4 className="font-display font-bold text-base text-foreground">
              Premium Feature
            </h4>
            <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
              Job Journey gives you AI-powered interview prep and offer
              analysis for every tracked application. Subscribe to unlock.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 max-w-sm mx-auto mt-4">
            {[
              { icon: MessageSquare, label: "Interview prep" },
              { icon: DollarSign, label: "Salary benchmarks" },
              { icon: AlertTriangle, label: "Red flag alerts" },
              { icon: HelpCircle, label: "Questions to ask" },
            ].map((item) => (
              <div
                key={item.label}
                className="flex items-center gap-2 p-2 rounded-lg bg-card border border-border text-xs text-muted-foreground"
              >
                <item.icon className="w-3.5 h-3.5 text-primary" />
                {item.label}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const currentInsight = selectedApp ? insights[selectedApp.id] : null;

  return (
    <div className="space-y-6 animate-fade-up">
      <div>
        <h3 className="font-display font-bold text-lg text-foreground">
          Job Journey
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          Track your applications and get AI insights at each stage. Select a
          job in{" "}
          <span className="font-medium text-foreground">Interview</span> or{" "}
          <span className="font-medium text-foreground">Offered</span> to load
          guidance.
        </p>
      </div>

      {trackedJobs.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-border p-8 text-center space-y-3">
          <Briefcase className="w-10 h-10 mx-auto text-muted-foreground/30" />
          <p className="text-sm text-muted-foreground">
            No tracked applications yet. Add jobs from the Applications tab to
            see them here.
          </p>
        </div>
      ) : (
        <div className="flex flex-col lg:flex-row gap-6">
          <div className="w-full lg:w-72 shrink-0 space-y-4">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">
                Salary benchmark region
              </label>
              <Select
                value={selectedRegion}
                onValueChange={setSelectedRegion}
              >
                <SelectTrigger className="h-8 text-xs">
                  <Globe className="w-3 h-3 mr-1" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REGIONS.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {stages.map((stage) => {
              const stageJobs = trackedJobs.filter(
                (a) => a.status === stage
              );
              if (stageJobs.length === 0) return null;

              return (
                <div key={stage}>
                  <div className="flex items-center gap-2 mb-2">
                    <span
                      className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS_STYLES[stage]}`}
                    >
                      {STAGE_LABELS[stage]}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {stageJobs.length}
                    </span>
                  </div>

                  <div className="space-y-1.5">
                    {stageJobs.map((app) => {
                      const isSelected = selectedApp?.id === app.id;
                      const hasInsights = Boolean(insights[app.id]);
                      const canGetInsights =
                        stage === "interviewing" || stage === "offered";

                      return (
                        <button
                          key={app.id}
                          type="button"
                          onClick={() =>
                            canGetInsights
                              ? void handleGetInsights(app)
                              : setSelectedApp(app)
                          }
                          className={`w-full text-left p-3 rounded-lg border transition-colors ${
                            isSelected
                              ? "border-primary bg-primary/5"
                              : "border-border bg-card hover:bg-muted/50"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-foreground truncate">
                                {app.position}
                              </p>
                              <p className="text-xs text-muted-foreground truncate">
                                {app.company}
                              </p>
                            </div>
                            <div className="flex items-center gap-1 shrink-0 ml-2">
                              {hasInsights && (
                                <Sparkles className="w-3 h-3 text-primary" />
                              )}
                              {canGetInsights && (
                                <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex-1 min-w-0">
            {!selectedApp ? (
              <div className="rounded-xl border-2 border-dashed border-border p-8 text-center min-h-[200px] flex flex-col items-center justify-center">
                <Sparkles className="w-8 h-8 text-muted-foreground/30 mb-3" />
                <p className="text-sm text-muted-foreground">
                  Select a job in Interview or Offered to get AI insights.
                </p>
              </div>
            ) : loading ? (
              <div className="rounded-xl border border-border bg-card p-8 text-center">
                <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">
                  Generating insights for {selectedApp.position} at{" "}
                  {selectedApp.company}...
                </p>
              </div>
            ) : currentInsight ? (
              <div className="space-y-4">
                <div className="rounded-xl bg-gradient-to-br from-primary to-primary/80 p-4 text-primary-foreground">
                  <h4 className="font-display font-bold text-base">
                    {selectedApp.position}
                  </h4>
                  <p className="text-sm opacity-80">{selectedApp.company}</p>
                  <span className="inline-block mt-2 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/20">
                    {currentInsight.stage === "interviewing"
                      ? "Interview Prep"
                      : "Offer Analysis"}
                  </span>
                </div>

                {currentInsight.stage === "interviewing" &&
                  currentInsight.data && (
                    <InterviewInsights data={currentInsight.data} />
                  )}

                {currentInsight.stage === "offered" && currentInsight.data && (
                  <OfferInsights data={currentInsight.data} />
                )}
              </div>
            ) : selectedApp.status !== "interviewing" &&
              selectedApp.status !== "offered" ? (
              <div className="rounded-xl border border-border bg-card p-6 text-center">
                <p className="text-sm text-muted-foreground">
                  Insights are available when a job is in{" "}
                  <span className="font-medium text-foreground">Interview</span>{" "}
                  or{" "}
                  <span className="font-medium text-foreground">Offered</span>.
                </p>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

function InterviewInsights({ data }: { data: InsightPayload }) {
  const companyOverview =
    typeof data.companyOverview === "string" ? data.companyOverview : "";
  const culturalFit = Array.isArray(data.culturalFit) ? data.culturalFit : [];
  const technicalSkills = Array.isArray(data.technicalSkills)
    ? data.technicalSkills
    : [];
  const questionsToAsk = Array.isArray(data.questionsToAsk)
    ? data.questionsToAsk
    : [];
  const redFlags = Array.isArray(data.redFlags) ? data.redFlags : [];
  const tips = typeof data.tips === "string" ? data.tips : "";

  return (
    <div className="space-y-4">
      {companyOverview && (
        <div className="rounded-xl border border-border bg-card p-4">
          <h5 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            Company Overview
          </h5>
          <p className="text-sm text-foreground leading-relaxed">
            {companyOverview}
          </p>
        </div>
      )}

      {culturalFit.length > 0 && (
        <InsightSection
          title="Cultural Fit — What to Expect"
          icon={MessageSquare}
          items={culturalFit as Array<{ topic: string; insight: string }>}
        />
      )}

      {technicalSkills.length > 0 && (
        <InsightSection
          title="Skills Review — How to Prepare"
          icon={CheckCircle}
          items={technicalSkills as Array<{ topic: string; insight: string }>}
        />
      )}

      {questionsToAsk.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="flex items-center gap-2 mb-3">
            <HelpCircle className="w-4 h-4 text-primary" />
            <h5 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Smart Questions to Ask
            </h5>
          </div>
          <div className="space-y-2">
            {questionsToAsk.map((q, i) => {
              const text = typeof q === "string" ? q : String(q);
              return (
                <div
                  key={i}
                  className="flex items-start gap-2 p-2.5 rounded-lg bg-primary/5 border border-primary/10"
                >
                  <span className="text-xs text-primary font-medium mt-0.5">
                    {i + 1}.
                  </span>
                  <p className="text-sm text-foreground flex-1">{text}</p>
                  <button
                    type="button"
                    onClick={() => {
                      void navigator.clipboard.writeText(text);
                      toast.success("Copied!");
                    }}
                    className="shrink-0 text-muted-foreground hover:text-primary"
                  >
                    <Copy className="w-3 h-3" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {redFlags.length > 0 && (
        <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            <h5 className="text-xs font-semibold text-amber-700 dark:text-amber-300 uppercase tracking-wider">
              Watch Out For
            </h5>
          </div>
          <ul className="space-y-1.5">
            {redFlags.map((flag, i) => (
              <li
                key={i}
                className="text-sm text-amber-800 dark:text-amber-200 flex items-start gap-2"
              >
                <span className="text-amber-500 mt-1">·</span>
                {typeof flag === "string" ? flag : String(flag)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {tips && (
        <div className="rounded-lg bg-muted/50 border border-border p-3">
          <p className="text-xs text-muted-foreground italic">{tips}</p>
        </div>
      )}
    </div>
  );
}

function OfferInsights({ data }: { data: InsightPayload }) {
  const salaryRange = data.salaryRange as
    | {
        region?: string;
        low?: string;
        mid?: string;
        high?: string;
        notes?: string;
      }
    | undefined;
  const offerChecklist = Array.isArray(data.offerChecklist)
    ? data.offerChecklist
    : [];
  const negotiationTips = Array.isArray(data.negotiationTips)
    ? data.negotiationTips
    : [];
  const benefitsToVerify = Array.isArray(data.benefitsToVerify)
    ? data.benefitsToVerify
    : [];
  const redFlags = Array.isArray(data.redFlags) ? data.redFlags : [];
  const summary = typeof data.summary === "string" ? data.summary : "";

  return (
    <div className="space-y-4">
      {salaryRange && (
        <div className="rounded-xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 p-4">
          <div className="flex items-center gap-2 mb-3">
            <DollarSign className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
            <h5 className="text-xs font-semibold text-emerald-700 dark:text-emerald-300 uppercase tracking-wider">
              Salary Range — {salaryRange.region ?? "Region"}
            </h5>
          </div>
          <div className="grid grid-cols-3 gap-3 mb-3">
            {[
              { label: "Low", value: salaryRange.low },
              { label: "Mid", value: salaryRange.mid },
              { label: "High", value: salaryRange.high },
            ].map((tier) => (
              <div
                key={tier.label}
                className="text-center p-2 rounded-lg bg-emerald-100/50 dark:bg-emerald-800/30"
              >
                <p className="text-[10px] text-emerald-600 dark:text-emerald-400 uppercase font-semibold">
                  {tier.label}
                </p>
                <p className="text-sm font-bold text-emerald-800 dark:text-emerald-200 mt-0.5">
                  {tier.value ?? "—"}
                </p>
              </div>
            ))}
          </div>
          {salaryRange.notes && (
            <p className="text-xs text-emerald-700 dark:text-emerald-300">
              {salaryRange.notes}
            </p>
          )}
        </div>
      )}

      {offerChecklist.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle className="w-4 h-4 text-primary" />
            <h5 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Offer Checklist
            </h5>
          </div>
          <div className="space-y-2">
            {offerChecklist.map((raw, i) => {
              const item = raw as { item?: string; why?: string };
              return (
                <div
                  key={i}
                  className="p-3 rounded-lg bg-muted/50 border border-border"
                >
                  <p className="text-sm font-medium text-foreground">
                    {item.item}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {item.why}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {negotiationTips.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="flex items-center gap-2 mb-3">
            <MessageSquare className="w-4 h-4 text-primary" />
            <h5 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Negotiation Scripts
            </h5>
          </div>
          <div className="space-y-3">
            {negotiationTips.map((raw, i) => {
              const tip = raw as { tip?: string; script?: string };
              const script = tip.script ?? "";
              return (
                <div key={i} className="space-y-1.5">
                  <p className="text-sm font-medium text-foreground">
                    {tip.tip}
                  </p>
                  <div className="flex items-start gap-2 p-2.5 rounded-lg bg-primary/5 border border-primary/10">
                    <p className="text-sm text-foreground italic flex-1">
                      &ldquo;{script}&rdquo;
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        void navigator.clipboard.writeText(script);
                        toast.success("Copied!");
                      }}
                      className="shrink-0 text-muted-foreground hover:text-primary mt-0.5"
                    >
                      <Copy className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {benefitsToVerify.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-4">
          <h5 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            Benefits to Verify
          </h5>
          <div className="flex flex-wrap gap-2">
            {benefitsToVerify.map((b, i) => (
              <span
                key={i}
                className="px-2.5 py-1 rounded-full bg-muted text-xs font-medium text-foreground"
              >
                {typeof b === "string" ? b : String(b)}
              </span>
            ))}
          </div>
        </div>
      )}

      {redFlags.length > 0 && (
        <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            <h5 className="text-xs font-semibold text-amber-700 dark:text-amber-300 uppercase tracking-wider">
              Offer Red Flags
            </h5>
          </div>
          <ul className="space-y-1.5">
            {redFlags.map((flag, i) => (
              <li
                key={i}
                className="text-sm text-amber-800 dark:text-amber-200 flex items-start gap-2"
              >
                <span className="text-amber-500 mt-1">·</span>
                {typeof flag === "string" ? flag : String(flag)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {summary && (
        <div className="rounded-lg bg-muted/50 border border-border p-3">
          <p className="text-xs text-muted-foreground italic">{summary}</p>
        </div>
      )}
    </div>
  );
}

function InsightSection({
  title,
  icon: Icon,
  items,
}: {
  title: string;
  icon: LucideIcon;
  items: Array<{ topic: string; insight: string }>;
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-primary" />
        <h5 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          {title}
        </h5>
      </div>
      <div className="space-y-2">
        {items.map((item, i) => (
          <div
            key={i}
            className="p-3 rounded-lg bg-muted/50 border border-border"
          >
            <p className="text-sm font-medium text-foreground">{item.topic}</p>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              {item.insight}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
